import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { KeyboardService } from '../keyboard/keyboard.service';

@Component({
  selector: 'app-keyboard',
  templateUrl: './keyboard.component.html',
  styleUrls: ['./keyboard.component.scss']
})
export class KeyboardComponent implements OnInit, OnDestroy {
  uyirKeys: string[] = [];
  meiKeys: string[][] = [
    ['க்', 'ச்', 'ட்', 'த்', 'ப்', 'ற்'],
    ['ங்', 'ஞ்', 'ண்', 'ந்', 'ம்', 'ன்'],
    ['ய்', 'ர்', 'ல்', 'வ்', 'ழ்', 'ள்']
  ];

  lastKey: string | null = null;
  private lastKeySubscription!: Subscription;

  private englishToTamilMap: { [key: string]: string } = {
    'q': 'அ', 'w': 'ஆ', 'e': 'இ', 'r': 'ஈ', 'a': 'உ', 's': 'ஊ', 
    'd': 'எ', 'f': 'ஏ', 'z': 'ஐ', 'x': 'ஒ', 'c': 'ஓ', 'v': 'ஔ',
    't': 'க்', 'y': 'ச்', 'u': 'ட்', 'i': 'த்', 'o': 'ப்', 'p': 'ற்',
    'g': 'ங்', 'h': 'ஞ்', 'j': 'ண்', 'k': 'ந்', 'l': 'ம்', ';': 'ன்',
    'b': 'ய்', 'n': 'ர்', 'm': 'ல்', ',': 'வ்', '.': 'ழ்', '/': 'ள்', '`': 'ஃ'
  };

  constructor(private keyboardService: KeyboardService) { }

  ngOnInit() {
    this.lastKeySubscription = this.keyboardService.lastKey$.subscribe(lastKey => {
      this.lastKey = lastKey;
      this.uyirKeys = this.isMei(lastKey) ? ['', 'ா', 'ி', 'ீ', 'ு', 'ூ', 'ெ', 'ே', 'ை', 'ொ', 'ோ', 'ௌ'] : ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];
    });
  }

  getUyirKeys(): string[] {
    return this.uyirKeys;
  }

  displayUyirKey(key: string): string {
    if (this.isMei(this.lastKey) && this.lastKey) {
      return this.lastKey[0] + key;
    }
    return key;
  }

  isMei(char: string | null): boolean {
    if (!char) return false;
    return ['க்', 'ச்', 'ட்', 'த்', 'ப்', 'ற்', 'ங்', 'ஞ்', 'ண்', 'ந்', 'ம்', 'ன்', 'ய்', 'ர்', 'ல்', 'வ்', 'ழ்', 'ள்'].includes(char);
  }

  onKeyClicked(value: string) {
    if (value === 'DELETE') {
      this.keyboardService.onDelete();
      this.uyirKeys = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];
      this.lastKey = null;
    } else if (value === 'SPACE') {
      this.keyboardService.onSpace();
      this.uyirKeys = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];
      this.lastKey = null;
    } else if (value === 'ENTER') {
      this.keyboardService.onEnter();
      this.uyirKeys = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];
      this.lastKey = null;
      this.keyboardService.clearLastKey();
    } else {
      // Convert the key to Tamil if it's an English key
      const tamilChar = this.englishToTamilMap[value] || value;

      // Check if the last key was a Mei and the current key is an Uyir
      if (this.isMei(this.lastKey) && this.isUyir(tamilChar)) {
        // Combine Mei + Uyir into UyirMei
        const uyirMeiCombination = this.getUyirMeiCombination(tamilChar);
        if (uyirMeiCombination) {
          this.keyboardService.onChar(uyirMeiCombination);
          return; // Exit after handling the combination
        }
      }

      // Default behavior for other keys
      this.keyboardService.onChar(tamilChar);
    }
  }

  // Helper method to check if a character is an Uyir (vowel)
  isUyir(char: string): boolean {
    const uyirChars = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];
    return uyirChars.includes(char);
  }

  // Helper method to get the UyirMei combination
  getUyirMeiCombination(uyir: string): string | null {
    if (this.lastKey && this.isMei(this.lastKey)) {
      const baseConsonant = this.lastKey[0]; // Get the base consonant without pulli
      const uyirSymbolMap: { [key: string]: string } = {
        'அ': '', 'ஆ': 'ா', 'இ': 'ி', 'ஈ': 'ீ', 'உ': 'ு', 'ஊ': 'ூ',
        'எ': 'ெ', 'ஏ': 'ே', 'ஐ': 'ை', 'ஒ': 'ொ', 'ஓ': 'ோ', 'ஔ': 'ௌ'
      };
      const symbol = uyirSymbolMap[uyir];
      if (symbol !== undefined) {
        return baseConsonant + symbol; // Combine base consonant with Uyir symbol
      }
    }
    return null;
  }

  ngOnDestroy() {
    this.lastKeySubscription.unsubscribe();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    // Skip handling on mobile devices except for special keys
    if (this.isMobilePlatform() && event.key !== 'Backspace' && event.key !== 'Enter') {
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      this.keyboardService.onDelete();
      this.uyirKeys = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];
      this.lastKey = null;
      this.keyboardService.clearLastKey();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.keyboardService.onEnter();
      this.uyirKeys = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];
      this.lastKey = null;
      this.keyboardService.clearLastKey();
    }
  }

  @HostListener('window:keypress', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Skip handling on mobile devices completely
    if (this.isMobilePlatform()) {
      return;
    }

    const key = event.key.toLowerCase();
    
    if (this.englishToTamilMap.hasOwnProperty(key)) {
      event.preventDefault();
      const tamilChar = this.englishToTamilMap[key];
      
      // If the last key was a consonant (ends with ்)
      if (this.lastKey && this.lastKey.endsWith('்')) {
        // If current key maps to a vowel (அ, ஆ, etc.)
        if ('அஆஇஈஉஊஎஏஐஒஓஔ'.includes(tamilChar)) {
          const baseConsonant = this.lastKey.slice(0, -1);
          const uyirToSymbolMap: { [key: string]: string } = {
            'அ': '', 'ஆ': 'ா', 'இ': 'ி', 'ஈ': 'ீ', 'உ': 'ு', 'ஊ': 'ூ',
            'எ': 'ெ', 'ஏ': 'ே', 'ஐ': 'ை', 'ஒ': 'ொ', 'ஓ': 'ோ', 'ஔ': 'ௌ'
          };
          
          // Combine the consonant with the vowel symbol
          const combinedChar = baseConsonant + uyirToSymbolMap[tamilChar];
          
          // Update the input value by removing the last consonant with pulli
          let currentValue = this.keyboardService.getInputValue();
          currentValue = currentValue.slice(0, -2) + combinedChar;
          this.keyboardService.setInputValue(currentValue);
          
          // Play audio for the combination
          this.keyboardService.playAudioForChar(combinedChar);
          return;
        }
      }
      
      // Normal processing for other cases
      this.keyboardService.onChar(tamilChar);
    }
    // Let all other keys pass through
  }

  // Helper method to check if we're on a mobile device
  private isMobilePlatform(): boolean {
    const isTouchDevice = navigator.maxTouchPoints ? navigator.maxTouchPoints > 2 : false;
    return /iPhone|iPad|iPod|Android|tablet|Tablet/i.test(navigator.userAgent) || isTouchDevice;
  }
}